'use client'

import { useTranslation } from 'react-i18next'
import { WhiteLabelAssets, BrandedContent } from '../../white-label-assets'
import { LoginVideo } from '../login-video'
import { LoginCarousel } from '../login-carousel'
import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useThemeStore } from '@/lib/stores/theme-store'

interface RightWithVideoLayoutProps {
  children: React.ReactNode
  titleShowcase: string
  brandName: string
  videoUrl: string
  banners?: string | string[]
}

export function RightWithVideoLayout({
  children,
  titleShowcase,
  brandName,
  videoUrl,
  banners
}: RightWithVideoLayoutProps) {
  const { t } = useTranslation(['auth'])
  const { currentTheme } = useThemeStore()
  const objectPosition = currentTheme?.login?.layout?.body?.imagePosition || 'center'
  const logoNavOnly =
    currentTheme?.login?.features?.logoNavOnly ||
    (currentTheme as any)?.showcase_content?.login?.logoNavOnly

  // Novas propriedades de título e descrição
  const showTitle = currentTheme?.login?.features?.showTitle ?? true
  const titleText = currentTheme?.login?.features?.titleText || currentTheme?.title
  const showDescription = currentTheme?.login?.features?.showDescription ?? false
  const descriptionText = currentTheme?.login?.features?.descriptionText || currentTheme?.description

  return (
    <div className="h-full relative rounded-xl">
      {/* Background with white-label image - fixo */}
      <div className="absolute inset-0 rounded-xl overflow-hidden m-2">
        <WhiteLabelAssets
          type="background"
          className="absolute inset-0 scale-100 origin-left w-full h-full"
          priority={true}
          alt="Login background"
          objectPosition={objectPosition}
        />
        <div className="absolute inset-0 bg-gradient-to-l from-background/20 to-transparent" />
      </div>

      {/* Container com scroll */}
      <div className="h-full overflow-y-auto">
        <div className="min-h-full flex items-center justify-center p-4 sm:p-8">
          {/* Wrapper para centralização vertical */}
          <div className="relative z-10 flex items-center justify-center w-full">
            {/* Container de conteúdo - altura determinada pelo card */}
            <div className="flex items-center justify-center gap-8 w-full container">
              {/* Carrossel - À esquerda */}
              {banners && (
                <div id="carousel-container" className="flex-1 min-w-0 flex flex-col gap-8"
                  style={{
                    gap: logoNavOnly ? 0 : 32
                  }}>
                  {/* Title and Description */}
                  {((showTitle && titleText) || (showDescription && descriptionText)) && (
                    <div className="space-y-2 mb-4">
                      {showTitle && titleText && (
                        <div
                          className="text-4xl font-bold"
                          style={{ color: 'var(--login-body-title)' }}
                          dangerouslySetInnerHTML={{ __html: titleText }}
                        />
                      )}
                      {showDescription && descriptionText && (
                        <div
                          className="text-lg"
                          style={{ color: 'var(--login-body-description)' }}
                          dangerouslySetInnerHTML={{ __html: descriptionText }}
                        />
                      )}
                    </div>
                  )}

                  {/* Carousel */}
                  <LoginCarousel banners={banners} />
                </div>
              )}

              {/* Login Card com vídeo integrado - À direita */}
              <div className="flex-shrink-0 flex-grow-0">
                <Card
                  className="max-w-sm md:min-w-[380px] w-full shadow-2xl backdrop-blur-sm border overflow-hidden"
                  style={{
                    backgroundColor: 'var(--login-form-bg)',
                    color: 'var(--login-form-text)',
                    borderColor: 'var(--login-form-border)',
                  }}
                >
                  {/* Vídeo no topo do card */}
                  {videoUrl && (
                    <div className="w-full aspect-video">
                      <LoginVideo url={videoUrl} className="h-full w-full" />
                    </div>
                  )}

                  {/* Header do Card */}
                  <CardHeader className="text-center flex flex-col items-start">
                    {!videoUrl && (
                      <div className="flex justify-start mb-20">
                        <BrandedContent fallback={<Skeleton className="h-7 w-32" />}>
                          <WhiteLabelAssets
                            type="logo-form"
                            className="h-7 w-auto"
                            alt={`${brandName} Logo`}
                          />
                        </BrandedContent>
                      </div>
                    )}

                    <div className="space-y-1">
                      <CardTitle
                        className="text-3xl font-bold text-left"
                        style={{ color: 'var(--login-body-title)' }}
                      >
                        {titleShowcase}
                      </CardTitle>
                    </div>

                    <CardDescription
                      className="text-left"
                      style={{ color: 'var(--login-body-description)' }}
                    >
                      {t('auth:login.subtitle')}
                    </CardDescription>
                  </CardHeader>

                  {/* Conteúdo do Card */}
                  <CardContent
                    style={{
                      backgroundColor: 'var(--login-form-bg)',
                      color: 'var(--login-form-text)',
                    }}
                  >
                    {children}
                  </CardContent>

                  {/* Login footer */}
                  <div
                    className="px-6 py-4 text-center text-xs"
                    style={{
                      backgroundColor: 'var(--login-form-footer-bg)',
                      color: 'var(--login-form-footer-text)',
                    }}
                  >
                    {t('auth:login.noAccount')}{' '}
                    <a
                      href="/cadastro"
                      style={{ color: 'var(--login-form-register)' }}
                      className="font-medium hover:underline"
                    >
                      {t('auth:login.createAccount')}
                    </a>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
